import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase';

/**
 * Servicio de autenticación utilizando Firebase Auth
 * Centraliza todas las operaciones de autenticación
 */
export const authService = {
  /**
   * Inicia sesión con email y contraseña
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise<UserCredential>} Credencial del usuario
   */
  async loginWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  },

  /**
   * Registra un nuevo usuario con email y contraseña
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @param {string} displayName - Nombre a mostrar del usuario
   * @returns {Promise<UserCredential>} Credencial del usuario
   */
  async registerWithEmail(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Actualizar el perfil del usuario con el nombre
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }
      
      // Enviar email de verificación
      await sendEmailVerification(userCredential.user);
      
      return userCredential;
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  },

  /**
   * Cierra la sesión del usuario actual
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error en logout:', error);
      throw error;
    }
  },

  /**
   * Envía un email para resetear la contraseña
   * @param {string} email - Email del usuario
   * @returns {Promise<void>}
   */
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error en reset password:', error);
      throw error;
    }
  },

  /**
   * Actualiza el perfil del usuario actual
   * @param {Object} profileData - Datos del perfil a actualizar
   * @returns {Promise<void>}
   */
  async updateUserProfile(profileData) {
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, profileData);
      } else {
        throw new Error('No hay usuario autenticado');
      }
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      throw error;
    }
  },

  /**
   * Reenvía el email de verificación
   * @returns {Promise<void>}
   */
  async resendEmailVerification() {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      } else {
        throw new Error('No hay usuario autenticado');
      }
    } catch (error) {
      console.error('Error enviando verificación:', error);
      throw error;
    }
  },

  /**
   * Obtiene el usuario actual
   * @returns {User|null} Usuario actual o null si no está autenticado
   */
  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * Verifica si hay un usuario autenticado
   * @returns {boolean} True si hay usuario autenticado
   */
  isAuthenticated() {
    return !!auth.currentUser;
  },

  /**
   * Escucha cambios en el estado de autenticación
   * @param {Function} callback - Función que se ejecuta cuando cambia el estado
   * @returns {Function} Función para desuscribirse
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  /**
   * Inicia sesión con Google usando popup flow
   * @returns {Promise<UserCredential>} Credencial del usuario
   */
  async loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      
      // Configurar scopes necesarios
      provider.addScope('email');
      provider.addScope('profile');
      
      // Configurar parámetros para mejor UX
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      return result;
    } catch (error) {
      // Manejar errores específicos de popup
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Autenticación cancelada por el usuario');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup bloqueado. Por favor permite popups para este sitio');
      }
      throw error;
    }
  },
  async getAccessToken() {
    try {
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }
      throw new Error('No hay usuario autenticado');
    } catch (error) {
      console.error('Error obteniendo token:', error);
      throw error;
    }
  },

  /**
   * Verifica si el email del usuario está verificado
   * @returns {boolean} True si el email está verificado
   */
  isEmailVerified() {
    return auth.currentUser?.emailVerified || false;
  }
};

export default authService;
